/**
 * Error categories returned from external services
 */
export const ERROR_CATEGORIES = {
  /** Infrastructure capacity errors - do not retry */
  INFRA_CAPACITY: 'infra.capacity',
  /** Bad host, retry and maybe get a good one  */
  INFRA_BAD_HOST: 'infra.bad_host',
  /** Publishing integrity errors - expected user errors */
  PUBLISHING_INTEGRITY: 'publishing.integrity',
} as const;

/**
 * Standardized error categories from STEFI APIs.
 * These are returned in the `error_category` field of STEFI responses.
 *
 * Simplified to 2 categories:
 * - INTERNAL (500): Server errors, unexpected failures
 * - INVALID_INPUT (400): Validation errors, malformed OIL handles
 */
export const STEFI_ERROR_CATEGORIES = {
  /** Internal server error - unexpected failures */
  INTERNAL: 'internal',
  /** Invalid input - validation errors, malformed OIL handles */
  INVALID_INPUT: 'invalid_input',
  /** Safety - content policy violation */
  SAFETY: 'safety',
  /** Rate limited - HTTP 429 or throttled by STEFI */
  RATE_LIMIT: 'rate_limit',
  /** Infrastructure capacity exhausted - GPU fleet overloaded, not user's fault */
  MEDIA_CAPACITY: 'media_capacity',
  /** OIL image file deleted — handle was cascade-deleted by TAO entity GC */
  OIL_IMAGE_FILE_DELETED: 'oil_image_file_deleted',
  /** OIL video file deleted — handle was cascade-deleted by TAO entity GC */
  OIL_VIDEO_FILE_DELETED: 'oil_video_file_deleted',
  /** Generation canceled — STEFI SSE terminal state (D96830904). Defensive: may not be emitted today. */
  CANCELED: 'canceled',
  /** Rate-limit cascade failure — STEFI SSE terminal state (D96830904). Distinct from request-level RATE_LIMIT. */
  RATE_LIMIT_FAILED: 'rate_limit_failed',
  /** Unclassified failure — catch-all for errors that don't match a specific category */
  UNKNOWN: 'unknown',
} as const;

export type StefiErrorCategory =
  (typeof STEFI_ERROR_CATEGORIES)[keyof typeof STEFI_ERROR_CATEGORIES];

export type ErrorCategory = (typeof ERROR_CATEGORIES)[keyof typeof ERROR_CATEGORIES];

// ============================================================================
// Internal status identifiers (not user-facing)
// ============================================================================

/** Quota exceeded status set on content items when STEFI returns OUT_OF_QUOTA. */
export const QUOTA_EXCEEDED_ERROR = 'out_of_quota' as const;

// ============================================================================
// User-friendly error messages (single source of truth)
// ============================================================================

export const ERROR_MESSAGES = {
  /** Service busy - capacity, rate limit, overload */
  BUSY: 'The service is busy. Please wait a moment and try again.',
  /** Rate limited - too many requests */
  RATE_LIMIT: 'Too many requests. Please wait a moment and try again.',
  /** Transient service issue - bad host, temporary unavailability */
  TEMPORARY: 'The generation service is temporarily unavailable. Please try again.',
  /** Request timed out */
  TIMEOUT: 'The request timed out. Please try again.',
  /** Content policy violation */
  INTEGRITY: 'This content could not be generated. Please try a different prompt.',
  /** Invalid input - bad parameters */
  INVALID_INPUT: 'The request was invalid. Please check your input and try again.',
  /** Infrastructure capacity - high demand, not user's fault */
  MEDIA_CAPACITY: 'We are experiencing unusually high demand. Please try again later.',
  /** OIL file expired/deleted — user needs to re-upload or use different media */
  OIL_FILE_EXPIRED: 'This media is no longer available. Please re-upload or use a different file.',
  /** Reference ent (oref/sref/setting/source) was deleted — user can retry without it */
  REFERENCE_DELETED:
    'A reference image used in this generation was deleted. Try regenerating without it.',
  /** Generic fallback */
  UNKNOWN: 'Something went wrong. Please try again.',
} as const;

/**
 * Maps backend error categories to user-friendly messages.
 * Used when the API returns an error_category field.
 */
export const USER_FRIENDLY_CATEGORY_MESSAGES: Record<ErrorCategory | 'unknown', string> = {
  [ERROR_CATEGORIES.INFRA_CAPACITY]: ERROR_MESSAGES.BUSY,
  [ERROR_CATEGORIES.INFRA_BAD_HOST]: ERROR_MESSAGES.TEMPORARY,
  [ERROR_CATEGORIES.PUBLISHING_INTEGRITY]: ERROR_MESSAGES.INTEGRITY,
  unknown: ERROR_MESSAGES.UNKNOWN,
};

/**
 * Maps STEFI error categories to user-friendly messages.
 *
 * Both categories map to generic messages since these are system integration
 * errors that users cannot fix themselves. The raw error_message is still
 * logged to Sentry/console for debugging.
 */
export const STEFI_CATEGORY_MESSAGES: Record<StefiErrorCategory, string> = {
  [STEFI_ERROR_CATEGORIES.INTERNAL]: ERROR_MESSAGES.UNKNOWN,
  [STEFI_ERROR_CATEGORIES.INVALID_INPUT]: ERROR_MESSAGES.UNKNOWN,
  [STEFI_ERROR_CATEGORIES.SAFETY]: ERROR_MESSAGES.INTEGRITY,
  [STEFI_ERROR_CATEGORIES.RATE_LIMIT]: ERROR_MESSAGES.RATE_LIMIT,
  [STEFI_ERROR_CATEGORIES.MEDIA_CAPACITY]: ERROR_MESSAGES.MEDIA_CAPACITY,
  [STEFI_ERROR_CATEGORIES.OIL_IMAGE_FILE_DELETED]: ERROR_MESSAGES.OIL_FILE_EXPIRED,
  [STEFI_ERROR_CATEGORIES.OIL_VIDEO_FILE_DELETED]: ERROR_MESSAGES.OIL_FILE_EXPIRED,
  [STEFI_ERROR_CATEGORIES.CANCELED]: ERROR_MESSAGES.UNKNOWN,
  [STEFI_ERROR_CATEGORIES.RATE_LIMIT_FAILED]: ERROR_MESSAGES.RATE_LIMIT,
  [STEFI_ERROR_CATEGORIES.UNKNOWN]: ERROR_MESSAGES.UNKNOWN,
};

// ============================================================================
// Pattern-based error classification (for raw error strings)
// ============================================================================

/**
 * Known error patterns from media generation services.
 * Patterns are matched against raw error strings (case-insensitive).
 */
export const MEDIA_GEN_ERROR_PATTERNS: ReadonlyArray<{ pattern: RegExp; message: string }> = [
  {
    // folly::BrokenPromise, Broken promise, etc.
    pattern: /BrokenPromise|Broken promise/i,
    message: ERROR_MESSAGES.TEMPORARY,
  },
  {
    // Rate limit / throttling: "exceeded the maximum number of requests", "too many requests"
    pattern: /exceeded.*maximum.*requests|too many requests/i,
    message: ERROR_MESSAGES.RATE_LIMIT,
  },
  {
    // APP_OVERLOAD, App Overloaded, batch queue is full, capacity issues, high demand
    pattern:
      /APP_OVERLOAD|App Overloaded|over capacity|at capacity|batch queue is full|experiencing high demand|cuda error/i,
    message: ERROR_MESSAGES.MEDIA_CAPACITY,
  },
  {
    pattern: /timed?\s*out|timeout/i,
    message: ERROR_MESSAGES.TIMEOUT,
  },
  {
    // OIL file not found / deleted / expired / sealed — user's image handle is gone.
    // Wrapped-OILException branches (OILException.*Deleted file, OILException.*File not found)
    // cover the wire shape STEFI rethrows for OIL tombstone refusals, which the coded-format
    // and class-name branches miss. See shared-brain stefi-oil-error-classifier-bug.md.
    pattern:
      /OIL Error\[FILE_NOT_FOUND|OIL Error\[FILE_DELETED|OIL Error\[FILE_SEALED|OILFileNotFoundException|OILFileDeletedException|OILFileExpiredException|OILFileSealedException|OILException.*Deleted file|OILException.*File not found|File not found.*OILException|File is in a sealed state|oil_image_file_deleted|oil_video_file_deleted/i,
    message: ERROR_MESSAGES.OIL_FILE_EXPIRED,
  },
  {
    // STEFI reference ent deleted — "<field>_ent_id <id> not found or not visible".
    // Hits oref/sref/setting/source_image/first_frame_image/source_video. Must run
    // before the generic /not found or not visible/ rule below so we surface a
    // reference-specific message instead of the OIL "re-upload" copy.
    pattern: /_ent_id\s+\S+\s+not found or not visible/i,
    message: ERROR_MESSAGES.REFERENCE_DELETED,
  },
  {
    // STEFI entity not found — ent ID was deleted/GC'd or wrong entity type
    pattern: /not found or not visible/i,
    message: ERROR_MESSAGES.OIL_FILE_EXPIRED,
  },
];

/**
 * Converts a media generation error to a user-friendly message.
 * Checks pattern matches first, then falls back to category-based messages.
 *
 * @param rawError - The raw error message from the service
 * @param errorCategory - Optional error category from the service (supports both legacy and STEFI categories)
 * @returns A user-friendly error message
 *
 * @example
 * getMediaGenErrorMessage('(APP_OVERLOAD) App Overloaded...') // => 'The service is busy...'
 * getMediaGenErrorMessage('unknown', 'infra.capacity') // => 'The service is busy...'
 * getMediaGenErrorMessage('handle cannot contain commas', 'invalid_input') // => 'Something went wrong...'
 */
export function getMediaGenErrorMessage(rawError: string, errorCategory?: string): string {
  // Check pattern matches first (most specific)
  for (const { pattern, message } of MEDIA_GEN_ERROR_PATTERNS) {
    if (pattern.test(rawError)) {
      return message;
    }
  }

  // Check STEFI error categories
  if (errorCategory && errorCategory in STEFI_CATEGORY_MESSAGES) {
    return STEFI_CATEGORY_MESSAGES[errorCategory as StefiErrorCategory];
  }

  // Fall back to legacy category-based message
  if (errorCategory && errorCategory in USER_FRIENDLY_CATEGORY_MESSAGES) {
    return USER_FRIENDLY_CATEGORY_MESSAGES[
      errorCategory as keyof typeof USER_FRIENDLY_CATEGORY_MESSAGES
    ];
  }

  // Default fallback
  return ERROR_MESSAGES.UNKNOWN;
}

/**
 * Check whether a user-friendly error message indicates a rate limit or
 * capacity issue that should NOT be surfaced as HTTP 500 to the client.
 *
 * Matches both user-level rate limits (429) and infrastructure capacity (503).
 * Use getErrorHttpStatus() for the correct status code.
 *
 * @deprecated Prefer getErrorHttpStatus() for new code — it returns the
 * correct status (429 vs 503) instead of a boolean.
 */
export function isRateLimitError(errorMsg: string): boolean {
  return (
    errorMsg === ERROR_MESSAGES.RATE_LIMIT ||
    errorMsg === ERROR_MESSAGES.MEDIA_CAPACITY ||
    errorMsg === ERROR_MESSAGES.BUSY
  );
}

/**
 * Check whether a user-friendly error message indicates an infrastructure
 * capacity issue that should be surfaced as HTTP 503 to the client.
 */
export function isCapacityError(errorMsg: string): boolean {
  return errorMsg === ERROR_MESSAGES.MEDIA_CAPACITY || errorMsg === ERROR_MESSAGES.BUSY;
}

/**
 * Get the appropriate HTTP status code for an error message.
 *
 * Use this in route handlers instead of `isRateLimitError(msg) ? 429 : 500`:
 * ```ts
 * const status = getErrorHttpStatus(errorMsg);
 * ```
 */
export function getErrorHttpStatus(errorMsg: string): number {
  if (errorMsg === ERROR_MESSAGES.RATE_LIMIT) return 429;
  if (errorMsg === ERROR_MESSAGES.MEDIA_CAPACITY || errorMsg === ERROR_MESSAGES.BUSY) return 503;
  if (errorMsg === ERROR_MESSAGES.OIL_FILE_EXPIRED) return 410;
  if (errorMsg === ERROR_MESSAGES.REFERENCE_DELETED) return 410;
  // Content-policy / safety rejections are 400 (matches CONTENT_POLICY in the
  // catalog and UPLOAD_CONTENT_POLICY on the upload path). New callers should
  // prefer classifyErrorCategory(category) which goes through the catalog
  // directly; this branch covers legacy string-only callers.
  if (errorMsg === ERROR_MESSAGES.INTEGRITY) return 400;
  return 500;
}

/**
 * Check whether a STEFI error category indicates a transient issue
 * (rate limit or GPU capacity) that should NOT mark a content item as failed.
 *
 * Used during polling to skip transient errors and let the next poll cycle retry.
 */
export function isTransientStefiError(errorCategory?: string): boolean {
  return (
    errorCategory === STEFI_ERROR_CATEGORIES.RATE_LIMIT ||
    errorCategory === STEFI_ERROR_CATEGORIES.MEDIA_CAPACITY
  );
}

/**
 * Check whether a STEFI error category indicates an OIL file that is
 * permanently gone (expired, deleted, GC'd). Not transient — the user
 * must re-upload or use a different image/video.
 */
export function isOilFileExpiredError(errorCategory?: string): boolean {
  return (
    errorCategory === STEFI_ERROR_CATEGORIES.OIL_IMAGE_FILE_DELETED ||
    errorCategory === STEFI_ERROR_CATEGORIES.OIL_VIDEO_FILE_DELETED
  );
}
